/**
 * VirtualizedCombobox — a searchable combobox with virtualized scrolling.
 *
 * Combines shadcn Popover + Command + @tanstack/react-virtual to handle
 * large option lists (100s+) without DOM bloat.
 */
import * as React from 'react'
import { useVirtualizer } from '@tanstack/react-virtual'
import { Check, ChevronsUpDown } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from '@/components/ui/command'

export interface ComboboxOption {
    value: string
    label: string
}

interface VirtualizedComboboxProps {
    /** Full list of options */
    options: ComboboxOption[]
    /** Currently selected value */
    value: string
    /** Called when the user picks an option */
    onValueChange: (value: string) => void
    /** Placeholder text shown in the search input */
    searchPlaceholder?: string
    /** Placeholder text shown when closed and nothing is selected */
    placeholder?: string
    /** Disable the control */
    disabled?: boolean
    /** Height of each virtual row in pixels */
    estimateSize?: number
    /** Max height of the dropdown in pixels */
    maxHeight?: number
}

function VirtualizedList({
    options,
    value,
    onSelect,
    estimateSize,
    maxHeight,
}: {
    options: ComboboxOption[]
    value: string
    onSelect: (value: string) => void
    estimateSize: number
    maxHeight: number
}) {
    const parentRef = React.useRef<HTMLDivElement>(null)

    const virtualizer = useVirtualizer({
        count: options.length,
        getScrollElement: () => parentRef.current,
        estimateSize: () => estimateSize,
        overscan: 8,
    })

    // Scroll to selected item on mount
    React.useEffect(() => {
        const idx = options.findIndex((o) => o.value === value)
        if (idx > 0) {
            virtualizer.scrollToIndex(idx, { align: 'center' })
        }
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <div
            ref={parentRef}
            style={{
                height: Math.min(options.length * estimateSize, maxHeight),
                overflow: 'auto',
            }}
        >
            <div
                style={{
                    height: virtualizer.getTotalSize(),
                    width: '100%',
                    position: 'relative',
                }}
            >
                {virtualizer.getVirtualItems().map((virtualRow) => {
                    const option = options[virtualRow.index]
                    return (
                        <CommandItem
                            key={option.value}
                            value={option.value}
                            onSelect={() => onSelect(option.value)}
                            style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: virtualRow.size,
                                transform: `translateY(${virtualRow.start}px)`,
                            }}
                        >
                            <Check
                                className={cn(
                                    'mr-2 h-4 w-4 shrink-0',
                                    value === option.value ? 'opacity-100' : 'opacity-0',
                                )}
                            />
                            <span className="whitespace-normal break-words leading-tight py-2.5">{option.label}</span>
                        </CommandItem>
                    )
                })}
            </div>
        </div>
    )
}

export function VirtualizedCombobox({
    options,
    value,
    onValueChange,
    searchPlaceholder = 'Search…',
    placeholder = 'Select…',
    disabled = false,
    estimateSize = 64,
    maxHeight = 300,
}: VirtualizedComboboxProps) {
    const [open, setOpen] = React.useState(false)
    const [search, setSearch] = React.useState('')

    // Filter options ourselves (shouldFilter=false on Command)
    const filtered = React.useMemo(() => {
        if (!search) return options
        const lower = search.toLowerCase()
        return options.filter(
            (o) =>
                o.label.toLowerCase().includes(lower) ||
                o.value.toLowerCase().includes(lower),
        )
    }, [options, search])

    const selectedLabel = options.find((o) => o.value === value)?.label ?? value

    return (
        <Popover open={open} onOpenChange={(v) => { setOpen(v); if (!v) setSearch('') }}>
            <PopoverTrigger asChild>
                <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={open}
                    className="w-full justify-between font-normal"
                    disabled={disabled}
                >
                    <span className="text-left whitespace-normal break-words line-clamp-1">{value ? selectedLabel : placeholder}</span>
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                <Command shouldFilter={false}>
                    <CommandInput
                        placeholder={searchPlaceholder}
                        value={search}
                        onValueChange={setSearch}
                    />
                    <CommandList>
                        {filtered.length === 0 && (
                            <CommandEmpty>No results found.</CommandEmpty>
                        )}
                        {filtered.length > 0 && (
                            <CommandGroup>
                                <VirtualizedList
                                    options={filtered}
                                    value={value}
                                    onSelect={(v) => {
                                        onValueChange(v)
                                        setOpen(false)
                                        setSearch('')
                                    }}
                                    estimateSize={estimateSize}
                                    maxHeight={maxHeight}
                                />
                            </CommandGroup>
                        )}
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    )
}
